import { useEffect } from 'react';
import { isTextEntryField, keepFocusedFieldVisible, shouldCenterFocusedField } from '@/lib/focusVisibility';
import { readVisualViewport } from '@/hooks/useVisualViewport';

const EDITABLE_FIELDS = 'input:not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="checkbox"]):not([type="radio"]):not([type="file"]):not([type="hidden"]):not([type="image"]), textarea, [contenteditable="true"]';

function isTouchViewport() {
  if (typeof window === 'undefined') return false;
  const coarsePointer = window.matchMedia?.('(pointer: coarse)').matches;
  const touchWithoutHover = (navigator.maxTouchPoints || 0) > 0 && window.matchMedia?.('(hover: none)').matches;
  return Boolean(coarsePointer || touchWithoutHover);
}

function getScrollableParent(element) {
  let parent = element?.parentElement;
  while (parent && parent !== document.body && parent !== document.documentElement) {
    const { overflowY } = window.getComputedStyle(parent);
    if (parent.scrollHeight > parent.clientHeight + 2) {
      if (/(auto|scroll|overlay)/.test(overflowY)) return parent;
      if (overflowY === 'hidden') {
        parent.style.overflowY = 'auto';
        return parent;
      }
    }
    parent = parent.parentElement;
  }
  return null;
}

function centerFocusedField() {
  const field = document.activeElement;
  if (!field?.matches?.(EDITABLE_FIELDS) || field.disabled || !isTouchViewport()) return;

  const viewport = window.visualViewport;
  const visibleTop = Math.max(0, viewport?.offsetTop || 0) + 12;
  const visibleBottom = Math.min(window.innerHeight, (viewport?.offsetTop || 0) + (viewport?.height || window.innerHeight)) - 12;
  let scroller = getScrollableParent(field);
  if (!scroller) {
    const dialog = field.closest('[role="dialog"]');
    const hasCustomSheetPosition = dialog && (dialog.style.bottom && dialog.style.bottom !== 'auto' || dialog.style.top === 'auto');
    if (dialog && !hasCustomSheetPosition) {
      dialog.style.top = `${(visibleTop + visibleBottom) / 2}px`;
      dialog.style.transform = 'translate(-50%, -50%)';
      dialog.style.maxHeight = `${Math.max(0, visibleBottom - visibleTop)}px`;
      dialog.style.overflowY = 'auto';
      scroller = dialog;
    }
  }
  const box = scroller?.getBoundingClientRect();
  const safeTop = Math.max(visibleTop, box?.top ?? visibleTop);
  const safeBottom = Math.min(visibleBottom, box?.bottom ?? visibleBottom);
  if (safeBottom <= safeTop) return;

  const rect = field.getBoundingClientRect();
  const delta = (rect.top + rect.bottom) / 2 - (safeTop + safeBottom) / 2;
  if (Math.abs(delta) < 3) return;
  if (scroller) scroller.scrollTop += delta;
  else window.scrollBy(0, delta);
}

function getFocusRoot(field) {
  // Radix popovers are anchored overlays; moving their content as a dialog
  // would detach it from the trigger. Let the shared page scroller handle them.
  if (field.closest('[data-radix-popper-content-wrapper]')) {
    return document.scrollingElement || document.documentElement;
  }
  return field.closest('[data-vaul-drawer], [role="dialog"]') || document.scrollingElement || document.documentElement;
}

function isSharedEditableField(field) {
  return isTextEntryField(field) && !field.disabled && !field.readOnly;
}

function setInlineStyle(element, property, value, savedStyles) {
  if (!savedStyles.has(property)) savedStyles.set(property, element.style[property]);
  element.style[property] = value;
}

function restoreInlineStyles(element, savedStyles) {
  savedStyles.forEach((value, property) => {
    element.style[property] = value;
  });
  savedStyles.clear();
}

function hasScrollableFieldAncestor(field, root) {
  for (let ancestor = field.parentElement; ancestor && ancestor !== root; ancestor = ancestor.parentElement) {
    if (ancestor.scrollHeight > ancestor.clientHeight + 1) {
      const overflowY = window.getComputedStyle(ancestor).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') return true;
    }
  }
  return false;
}

function centerFocusedFieldWithSharedVisibility(activeFocus) {
  const field = document.activeElement;
  if (!isSharedEditableField(field)) return;

  const viewport = readVisualViewport();
  if (!shouldCenterFocusedField(viewport)) {
    if (activeFocus.root) {
      restoreInlineStyles(activeFocus.root, activeFocus.styles);
      activeFocus.root = null;
    }
    return;
  }

  const root = getFocusRoot(field);
  if (activeFocus.root && activeFocus.root !== root) {
    restoreInlineStyles(activeFocus.root, activeFocus.styles);
  }
  activeFocus.root = root;
  if (root.matches?.('[role="dialog"], [data-vaul-drawer]')) {
    const bottomSheet = root.matches('[data-vaul-drawer]')
      || (window.getComputedStyle(root).top === 'auto' && window.getComputedStyle(root).bottom !== 'auto');
    const visibleHeight = Math.max(0, viewport.height - 24);
    setInlineStyle(root, 'maxHeight', `${visibleHeight}px`, activeFocus.styles);

    if (bottomSheet) {
      setInlineStyle(root, 'bottom', `${viewport.bottomInset}px`, activeFocus.styles);
    } else {
      setInlineStyle(root, 'top', `${viewport.top + viewport.height / 2}px`, activeFocus.styles);
      setInlineStyle(root, 'bottom', 'auto', activeFocus.styles);
      setInlineStyle(root, 'transform', 'translate(-50%, -50%)', activeFocus.styles);
    }

    // Preserve an existing nested scroller (for example, a drawer wizard pane).
    // Make the dialog itself scrollable only when it has overflowing content
    // and no descendant scroller can reveal the focused field.
    if (!hasScrollableFieldAncestor(field, root) && root.scrollHeight > root.clientHeight + 1) {
      setInlineStyle(root, 'overflowY', 'auto', activeFocus.styles);
    }
  }

  const center = shouldCenterFocusedField(viewport);
  const adjusted = keepFocusedFieldVisible({ root, field, viewport, center });
  if (!adjusted && root === (document.scrollingElement || document.documentElement)) {
    const visibleTop = Math.max(0, viewport.top) + 12;
    const visibleBottom = viewport.top + viewport.height - 12;
    const rect = field.getBoundingClientRect();
    const target = rect.height <= visibleBottom - visibleTop
      ? visibleTop + (visibleBottom - visibleTop - rect.height) / 2
      : visibleTop;
    const delta = rect.top - target;
    if (Math.abs(delta) > 2) window.scrollBy(0, delta);
  }
}

// Keep focused text fields in the visible part of the screen while the mobile
// keyboard opens, resizes, or pans the visual viewport. Works with page scrollers
// and scrollable dialog/drawer content, including portaled dialogs.
export default function useMobileInputVisibility(keepLegacyBehavior = false) {
  useEffect(() => {
    let frame = null;
    let timers = [];
    const activeFocus = { root: null, styles: new Map() };
    const viewport = window.visualViewport;
    const schedule = () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        frame = null;
        if (keepLegacyBehavior) centerFocusedField();
        else centerFocusedFieldWithSharedVisibility(activeFocus);
      });
    };
    const onFocusIn = event => {
      if (keepLegacyBehavior
        ? !event.target?.matches?.(EDITABLE_FIELDS)
        : !isSharedEditableField(event.target)) return;
      if (!keepLegacyBehavior) {
        const nextRoot = getFocusRoot(event.target);
        if (activeFocus.root && activeFocus.root !== nextRoot) {
          restoreInlineStyles(activeFocus.root, activeFocus.styles);
        }
        activeFocus.root = nextRoot;
      }
      timers.forEach(window.clearTimeout);
      timers = [80, 220, 420].map(delay => window.setTimeout(schedule, delay));
      schedule();
    };
    const onFocusOut = () => {
      timers.forEach(window.clearTimeout);
      timers = [];
      if (!keepLegacyBehavior) {
        window.setTimeout(() => {
          if (activeFocus.root && (!activeFocus.root.contains(document.activeElement)
            || !isSharedEditableField(document.activeElement))) {
            restoreInlineStyles(activeFocus.root, activeFocus.styles);
            activeFocus.root = null;
          }
        }, 0);
      }
    };

    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('focusout', onFocusOut, true);
    viewport?.addEventListener('resize', schedule);
    viewport?.addEventListener('scroll', schedule);
    window.addEventListener('orientationchange', schedule);
    window.addEventListener('resize', schedule);
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      timers.forEach(window.clearTimeout);
      document.removeEventListener('focusin', onFocusIn, true);
      document.removeEventListener('focusout', onFocusOut, true);
      viewport?.removeEventListener('resize', schedule);
      viewport?.removeEventListener('scroll', schedule);
      window.removeEventListener('orientationchange', schedule);
      window.removeEventListener('resize', schedule);
      if (!keepLegacyBehavior && activeFocus.root) {
        restoreInlineStyles(activeFocus.root, activeFocus.styles);
        activeFocus.root = null;
      }
    };
  }, [keepLegacyBehavior]);
}
